import { useEffect, useRef, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Skeleton from "@mui/material/Skeleton";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import { useTopVendorsByProducts } from "../hooks/useTopVendorsByProducts";
import { SearchToggle } from "@/components/ui/SearchToggle";
import { InstallSectionCard } from "../InstallSectionCard";
import { EmptyState } from "../../../../components/EmptyState";
import { ExportChart } from "../ExportChart";

type ViewMode = "10" | "20" | "50";

export const TopVendorsByProducts = () => {
  const company = useCompanyPageStore((state) => state.company);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<am4charts.XYChart | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("20");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = useTopVendorsByProducts({
    hgId: company?.hgId ?? "",
    skip: !company,
  });

  const topCount = parseInt(viewMode);

  const chartData = useMemo(() => {
    let filteredData = data?.topVendorsByProductCount || [];

    // Apply search filter
    if (searchTerm.trim()) {
      filteredData = filteredData.filter((item) =>
        item.vendorName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredData.slice(0, topCount).map((item) => ({
      vendor:
        item.vendorName.length > 25
          ? item.vendorName.substring(0, 25) + "..."
          : item.vendorName,
      fullName: item.vendorName,
      products: item.productCount,
    }));
  }, [data?.topVendorsByProductCount, topCount, searchTerm]);

  const csvExportData = useMemo(() => {
    return chartData.map((item) => ({
      vendor: item.fullName,
      productsCount: item.products,
    }));
  }, [chartData]);

  // Effect for chart creation (runs once when chartRef is available)
  useEffect(() => {
    if (!chartRef.current || chartInstanceRef.current) {
      return;
    }

    const chart = am4core.create(chartRef.current, am4charts.XYChart);
    chartInstanceRef.current = chart;

    // Create category axis
    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = "vendor";
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.minGridDistance = 25;
    categoryAxis.renderer.labels.template.rotation = -45;
    categoryAxis.renderer.labels.template.horizontalCenter = "right";
    categoryAxis.renderer.labels.template.verticalCenter = "middle";
    categoryAxis.renderer.labels.template.fontSize = 12;

    // Style X axis grid lines
    categoryAxis.renderer.grid.template.disabled = true;
    if (categoryAxis.tooltip) {
      categoryAxis.tooltip.disabled = true;
    }

    // Create value axis
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = "Number of Products";
    valueAxis.renderer.minWidth = 50;

    // Style Y axis grid lines
    valueAxis.renderer.grid.template.stroke = am4core.color("#000000");
    valueAxis.renderer.grid.template.strokeWidth = 1;
    valueAxis.renderer.grid.template.strokeDasharray = "3,3";

    // Create series
    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueY = "products";
    series.dataFields.categoryX = "vendor";
    series.name = "Products";
    series.tooltipText = "{fullName}: [bold]{valueY}[/] products";
    series.fillOpacity = 0.8;

    // Style columns with fixed color
    const columnTemplate = series.columns.template;
    columnTemplate.strokeWidth = 0;
    columnTemplate.strokeOpacity = 1;
    columnTemplate.fill = am4core.color("#81d4fa");

    // Add hover state
    const hoverState = columnTemplate.states.create("hover");
    hoverState.properties.fillOpacity = 0.9;
    hoverState.properties.fill = am4core.color("#4fc3f7");

    // Add cursor (tooltip only, no pan, no lines)
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "none";
    chart.cursor.xAxis = categoryAxis;
    chart.cursor.snapToSeries = series;
    chart.cursor.lineX.strokeOpacity = 0;
    chart.cursor.lineY.strokeOpacity = 0;

    // Set data
    chart.data = chartData;

    return () => {
      chart.dispose();
      chartInstanceRef.current = null;
    };
  }, [chartData]);

  // Effect for data updates (runs when chartData changes)
  useEffect(() => {
    if (!chartInstanceRef.current || isLoading) {
      return;
    }

    chartInstanceRef.current.data = chartData;
  }, [chartData, isLoading]);

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newViewMode: ViewMode | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const hasData = !isLoading && chartData.length > 0;
  const isEmpty = !isLoading && chartData.length === 0;

  const actions = (
    <>
      <SearchToggle
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        placeholder="Search vendor"
        width={200}
      />

      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        size="small"
      >
        <ToggleButton value="10">Top 10</ToggleButton>
        <ToggleButton value="20">Top 20</ToggleButton>
        <ToggleButton value="50">Top 50</ToggleButton>
      </ToggleButtonGroup>

      <ExportChart
        chartInstanceRef={chartInstanceRef}
        data={csvExportData}
        filename="top-vendors-by-products"
      />
    </>
  );

  return (
    <InstallSectionCard
      title="Top Vendors by Products"
      actions={actions}
      filterKey="TOP_VENDORS_BY_PRODUCTS"
    >
      <Box
        ref={chartRef}
        sx={{
          height: 360,
          width: "100%",
          display: hasData ? "block" : "none",
        }}
      />

      {isLoading && (
        <Box sx={{ height: 360 }}>
          <Skeleton variant="rectangular" width="100%" height="100%" />
        </Box>
      )}

      {isEmpty && (
        <EmptyState
          description={
            searchTerm.trim()
              ? `No vendors match "${searchTerm}"`
              : "No data available for this company at this time"
          }
        />
      )}
    </InstallSectionCard>
  );
};
